import { api } from './client';
import type { OrganizationKind } from './organizations';

export type UpdFunction = 'SCHFDOP' | 'SCHF' | 'DOP' | 'SCHFDOPGR';
export type EdoStatus = 'NOT_SENT' | 'QUEUED' | 'SENT' | 'DELIVERED' | 'SIGNED' | 'REJECTED' | 'REVOKED' | 'ERROR';

export const EDO_STATUS_LABELS: Record<EdoStatus, string> = {
  NOT_SENT: 'Не отправлен',
  QUEUED: 'В очереди',
  SENT: 'Отправлен',
  DELIVERED: 'Доставлен',
  SIGNED: 'Подписан',
  REJECTED: 'Отказ контрагента',
  REVOKED: 'Аннулирован',
  ERROR: 'Ошибка отправки',
};

export const EDO_STATUS_COLORS: Record<EdoStatus, string> = {
  NOT_SENT: 'gray',
  QUEUED: 'blue',
  SENT: 'blue',
  DELIVERED: 'cyan',
  SIGNED: 'green',
  REJECTED: 'red',
  REVOKED: 'red',
  ERROR: 'red',
};

export interface UpdLine {
  id: string;
  lineNumber: number;
  nomenclatureId: string | null;
  name: string;
  article: string | null;
  unitId: string | null;
  unitCode: string | null;
  unitSymbol: string | null;
  quantity: string;
  price: string;
  vatRateCode: string;
  vatRateValue: string | null;
  amount: string;
  vatAmount: string;
  totalAmount: string;
  exciseAmount: string | null;
  tnvedCode: string | null;
  countryCode: string | null;
  countryName: string | null;
  customsDeclarationNumber: string | null;
  itemKind: 'GOODS' | 'SERVICE' | 'WORK' | 'PROPERTY_RIGHT';
}

export interface UpdLineInput {
  nomenclatureId?: string | null;
  name: string;
  article?: string | null;
  unitId: string;
  quantity: string;
  price: string;
  vatRateId: string;
}

export interface UpdOrganizationRef {
  id: string;
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  inn: string;
  kpp: string | null;
  ogrn: string | null;
  ogrnipDate: string | null;
  addressFull: string | null;
  phone: string | null;
  email: string | null;
  directorPosition: string | null;
  directorName: string | null;
  accountantName: string | null;
  accountantIsDirector: boolean;
}

export interface UpdCounterpartyRef {
  id: string;
  shortName: string;
  fullName: string;
  kind: OrganizationKind;
  inn: string | null;
  kpp: string | null;
  addressFull: string | null;
  actualAddressFull: string | null;
}

export interface UpdContractRef {
  id: string;
  number: string;
  date: string;
}

export interface UpdInvoiceRef {
  id: string;
  number: string;
  date: string;
}

export interface Upd {
  id: string;
  organizationId: string;
  organization: UpdOrganizationRef;
  counterpartyId: string;
  counterparty: UpdCounterpartyRef;
  contractId: string | null;
  contract: UpdContractRef | null;
  invoiceId: string | null;
  invoice: UpdInvoiceRef | null;
  number: string;
  numberInt: number;
  date: string;
  function: UpdFunction;
  documentStatus: number;
  currencyCode: string;
  shipperIsSeller: boolean;
  shipperName: string | null;
  shipperAddress: string | null;
  consigneeIsBuyer: boolean;
  consigneeName: string | null;
  consigneeAddress: string | null;
  shipmentDocName: string | null;
  shipmentDocNumber: string | null;
  shipmentDocDate: string | null;
  transferBasis: string | null;
  governmentContractId: string | null;
  transferDate: string | null;
  signerPosition: string | null;
  signerName: string | null;
  signerAuthority: string | null;
  signerStatus: string | null;
  signerBasis: string | null;
  subtotal: string;
  vatAmount: string;
  total: string;
  edoStatus: EdoStatus;
  comment: string | null;
  lines: UpdLine[];
}

export interface UpdInput {
  date: string;
  transferDate?: string | null;
  transferBasis?: string | null;
  shipperIsSeller: boolean;
  shipperName?: string | null;
  shipperAddress?: string | null;
  consigneeIsBuyer: boolean;
  consigneeName?: string | null;
  consigneeAddress?: string | null;
  signerPosition?: string | null;
  signerName?: string | null;
  signerBasis?: string | null;
  comment?: string | null;
  lines: UpdLineInput[];
}

export interface UpdListItem {
  id: string;
  organizationId: string;
  organization: { id: string; shortName: string };
  counterpartyId: string;
  counterparty: { id: string; shortName: string };
  invoice: UpdInvoiceRef | null;
  number: string;
  date: string;
  total: string;
  edoStatus: EdoStatus;
}

export interface UpdListResponse {
  items: UpdListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface UpdListParams {
  search?: string;
  organizationId?: string;
  counterpartyId?: string;
  invoiceId?: string;
  page?: number;
  pageSize?: number;
}

export interface EdoDocflowEvent {
  at: string;
  event: string;
}

export interface EdoDocflow {
  id: string;
  updId: string;
  provider: string;
  externalId: string | null;
  status: EdoStatus;
  sentAt: string | null;
  deliveredAt: string | null;
  signedAt: string | null;
  rejectedAt: string | null;
  rejectReason: string | null;
  events: EdoDocflowEvent[] | null;
  lastError: string | null;
}

function buildQuery(params: UpdListParams): string {
  const search = new URLSearchParams();
  if (params.search) search.set('search', params.search);
  if (params.organizationId) search.set('organizationId', params.organizationId);
  if (params.counterpartyId) search.set('counterpartyId', params.counterpartyId);
  if (params.invoiceId) search.set('invoiceId', params.invoiceId);
  if (params.page !== undefined) search.set('page', String(params.page));
  if (params.pageSize !== undefined) search.set('pageSize', String(params.pageSize));
  const query = search.toString();
  return query ? `?${query}` : '';
}

export const updsApi = {
  list: (params: UpdListParams = {}) => api.get<UpdListResponse>(`/upds${buildQuery(params)}`),

  get: (id: string) => api.get<{ upd: Upd }>(`/upds/${id}`),

  createFromInvoice: (invoiceId: string) => api.post<{ upd: Upd }>(`/upds/from-invoice/${invoiceId}`),

  update: (id: string, data: UpdInput) => api.patch<{ upd: Upd }>(`/upds/${id}`, data),

  remove: (id: string) => api.delete<void>(`/upds/${id}`),

  sendEdo: (id: string) => api.post<{ upd: Upd; docflow: EdoDocflow }>(`/upds/${id}/send-edo`),

  getEdoStatus: (id: string) => api.get<{ docflow: EdoDocflow | null }>(`/upds/${id}/edo-status`),

  revokeEdo: (id: string) => api.post<{ upd: Upd }>(`/upds/${id}/revoke-edo`),
};
